/**
 * Advanced Fingerprinting Utility
 *
 * Collects browser/device signals similar to Fingerprint.com
 * Includes: Canvas, WebGL, Audio, Fonts, Hardware, and Incognito detection
 */

class AdvancedFingerprint {
    constructor() {
        this.signals = {};
    }

    /**
     * Collect all fingerprint signals
     * Returns comprehensive signal object for backend fuzzy matching
     */
    async collect() {
        console.log('🔍 Collecting advanced fingerprint signals...');

        try {
            const signals = {
                // High-entropy stable signals (most important)
                canvas: await this.getCanvasFingerprint(),
                webgl: await this.getWebGLFingerprint(),
                audio: await this.getAudioFingerprint(),
                fonts: this.getInstalledFonts(),

                // Medium-entropy signals
                screen: this.getScreenSignals(),
                hardware: this.getHardwareSignals(),
                timezone: this.getTimezoneSignals(),

                // Low-entropy signals (change frequently)
                browser: this.getBrowserSignals(),
                platform: navigator.platform,

                // Special detection
                isIncognito: await this.detectIncognito(),

                // Metadata
                timestamp: Date.now(),
                collectedAt: new Date().toISOString()
            };

            console.log('✅ Fingerprint signals collected:', {
                canvas: signals.canvas ? 'Yes' : 'No',
                webgl: signals.webgl ? 'Yes' : 'No',
                audio: signals.audio ? 'Yes' : 'No',
                fonts: signals.fonts ? signals.fonts.length + ' fonts' : 'No',
                isIncognito: signals.isIncognito
            });

            this.signals = signals;
            return signals;

        } catch (error) {
            console.error('❌ Error collecting fingerprint:', error);
            return this.getFallbackSignals();
        }
    }

    /**
     * Canvas Fingerprinting - GPU rendering differences
     * Most stable signal (survives incognito mode)
     */
    async getCanvasFingerprint() {
        try {
            const canvas = document.createElement('canvas');
            canvas.width = 200;
            canvas.height = 50;
            const ctx = canvas.getContext('2d');

            if (!ctx) return null;

            // Draw various shapes and text to maximize entropy
            ctx.textBaseline = 'top';
            ctx.font = '14px "Arial"';
            ctx.textBaseline = 'alphabetic';
            ctx.fillStyle = '#f60';
            ctx.fillRect(125, 1, 62, 20);
            ctx.fillStyle = '#069';
            ctx.fillText('Hello, Fingerprint! 🌍🔍', 2, 15);
            ctx.fillStyle = 'rgba(102, 204, 0, 0.7)';
            ctx.fillText('Advanced Signals 🚀', 4, 17);

            // Add some geometric shapes for additional entropy
            ctx.beginPath();
            ctx.arc(50, 25, 20, 0, Math.PI * 2, true);
            ctx.closePath();
            ctx.fill();

            // Return base64 data URL (last 100 chars for size efficiency)
            const dataURL = canvas.toDataURL();
            return dataURL.slice(-100);

        } catch (error) {
            console.warn('Canvas fingerprint failed:', error);
            return null;
        }
    }

    /**
     * WebGL Fingerprinting - GPU information
     * Very stable, survives incognito
     */
    async getWebGLFingerprint() {
        try {
            const canvas = document.createElement('canvas');
            const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');

            if (!gl) return null;

            const debugInfo = gl.getExtension('WEBGL_debug_renderer_info');

            return {
                vendor: debugInfo ? gl.getParameter(debugInfo.UNMASKED_VENDOR_WEBGL) : null,
                renderer: debugInfo ? gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL) : null,
                version: gl.getParameter(gl.VERSION),
                shadingLanguageVersion: gl.getParameter(gl.SHADING_LANGUAGE_VERSION),
                maxTextureSize: gl.getParameter(gl.MAX_TEXTURE_SIZE),
                maxViewportDims: gl.getParameter(gl.MAX_VIEWPORT_DIMS),
                supportedExtensions: gl.getSupportedExtensions()
            };

        } catch (error) {
            console.warn('WebGL fingerprint failed:', error);
            return null;
        }
    }

    /**
     * Audio Context Fingerprinting
     * Audio stack produces unique signals per hardware
     */
    async getAudioFingerprint() {
        try {
            const AudioContext = window.AudioContext || window.webkitAudioContext;
            if (!AudioContext) return null;

            const context = new AudioContext();
            const oscillator = context.createOscillator();
            const analyser = context.createAnalyser();
            const gainNode = context.createGain();
            const scriptProcessor = context.createScriptProcessor(4096, 1, 1);

            gainNode.gain.value = 0; // Mute
            oscillator.type = 'triangle';
            oscillator.connect(analyser);
            analyser.connect(scriptProcessor);
            scriptProcessor.connect(gainNode);
            gainNode.connect(context.destination);

            oscillator.start(0);

            return new Promise((resolve) => {
                const timeout = setTimeout(() => {
                    try {
                        oscillator.stop();
                        scriptProcessor.disconnect();
                        context.close();
                    } catch (e) {}
                    resolve(null);
                }, 100);

                scriptProcessor.onaudioprocess = function(event) {
                    clearTimeout(timeout);

                    const bins = event.inputBuffer.getChannelData(0);
                    let sum = 0;
                    for (let i = 0; i < bins.length; i++) {
                        sum += Math.abs(bins[i]);
                    }

                    const audioHash = sum.toString();

                    try {
                        oscillator.stop();
                        scriptProcessor.disconnect();
                        context.close();
                    } catch (e) {}

                    resolve(audioHash);
                };
            });

        } catch (error) {
            console.warn('Audio fingerprint failed:', error);
            return null;
        }
    }

    /**
     * Font Detection - Installed system fonts
     * Moderately stable
     */
    getInstalledFonts() {
        try {
            const baseFonts = ['monospace', 'sans-serif', 'serif'];
            const testFonts = [
                'Arial', 'Verdana', 'Comic Sans MS', 'Trebuchet MS', 'Times New Roman',
                'Georgia', 'Palatino', 'Garamond', 'Courier New', 'Brush Script MT',
                'Impact', 'Lucida Sans', 'Tahoma', 'Helvetica', 'Calibri', 'Century Gothic',
                'Futura', 'Monaco', 'Consolas', 'Ubuntu', 'Roboto', 'Open Sans'
            ];

            const testString = 'mmmmmmmmmmlli';
            const testSize = '72px';
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');

            if (!ctx) return [];

            // Measure width with base fonts
            const baseWidths = {};
            baseFonts.forEach(baseFont => {
                ctx.font = testSize + ' ' + baseFont;
                baseWidths[baseFont] = ctx.measureText(testString).width;
            });

            // Check which fonts are installed
            const installedFonts = [];
            testFonts.forEach(font => {
                let detected = false;
                baseFonts.forEach(baseFont => {
                    ctx.font = testSize + ' "' + font + '", ' + baseFont;
                    const width = ctx.measureText(testString).width;
                    if (width !== baseWidths[baseFont]) {
                        detected = true;
                    }
                });
                if (detected) {
                    installedFonts.push(font);
                }
            });

            return installedFonts;

        } catch (error) {
            console.warn('Font detection failed:', error);
            return [];
        }
    }

    /**
     * Screen Signals
     */
    getScreenSignals() {
        return {
            width: screen.width,
            height: screen.height,
            availWidth: screen.availWidth,
            availHeight: screen.availHeight,
            colorDepth: screen.colorDepth,
            pixelDepth: screen.pixelDepth,
            pixelRatio: window.devicePixelRatio,
            orientation: screen.orientation ? screen.orientation.type : null
        };
    }

    /**
     * Hardware Signals
     */
    getHardwareSignals() {
        return {
            cpuCores: navigator.hardwareConcurrency,
            deviceMemory: navigator.deviceMemory,
            maxTouchPoints: navigator.maxTouchPoints,
            vendor: navigator.vendor,
            platform: navigator.platform
        };
    }

    /**
     * Timezone Signals
     */
    getTimezoneSignals() {
        return {
            offset: new Date().getTimezoneOffset(),
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
        };
    }

    /**
     * Browser Signals (changes with updates)
     */
    getBrowserSignals() {
        return {
            userAgent: navigator.userAgent,
            language: navigator.language,
            languages: navigator.languages,
            cookieEnabled: navigator.cookieEnabled,
            doNotTrack: navigator.doNotTrack,
            plugins: this.getPlugins(),
            mimeTypes: this.getMimeTypes()
        };
    }

    /**
     * Get installed plugins
     */
    getPlugins() {
        try {
            const plugins = [];
            for (let i = 0; i < navigator.plugins.length; i++) {
                plugins.push(navigator.plugins[i].name);
            }
            return plugins;
        } catch (error) {
            return [];
        }
    }

    /**
     * Get MIME types
     */
    getMimeTypes() {
        try {
            const mimeTypes = [];
            for (let i = 0; i < navigator.mimeTypes.length; i++) {
                mimeTypes.push(navigator.mimeTypes[i].type);
            }
            return mimeTypes;
        } catch (error) {
            return [];
        }
    }

    /**
     * Incognito/Private Mode Detection
     * Multiple techniques for different browsers
     */
    async detectIncognito() {
        try {
            // Technique 1: FileSystem API quota check (Chrome, Edge)
            if ('storage' in navigator && 'estimate' in navigator.storage) {
                const {usage, quota} = await navigator.storage.estimate();
                // Incognito mode typically has quota < 120MB
                if (quota < 120000000) {
                    console.log('🕵️ Incognito detected via storage quota');
                    return true;
                }
            }

            // Technique 2: IndexedDB in Safari private mode
            return new Promise((resolve) => {
                const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);

                if (isSafari) {
                    try {
                        window.openDatabase(null, null, null, null);
                        resolve(false); // Regular mode allows openDatabase
                    } catch (e) {
                        console.log('🕵️ Incognito detected via Safari openDatabase');
                        resolve(true); // Incognito throws error
                    }
                } else {
                    // Chrome/Firefox: Check if quota is suspiciously low
                    resolve(false); // Default to false if no detection method works
                }
            });

        } catch (error) {
            console.warn('Incognito detection failed:', error);
            return false;
        }
    }

    /**
     * Fallback signals if main collection fails
     */
    getFallbackSignals() {
        return {
            canvas: null,
            webgl: null,
            audio: null,
            fonts: [],
            screen: this.getScreenSignals(),
            hardware: this.getHardwareSignals(),
            timezone: this.getTimezoneSignals(),
            browser: {
                userAgent: navigator.userAgent,
                language: navigator.language
            },
            platform: navigator.platform,
            isIncognito: false,
            timestamp: Date.now(),
            collectedAt: new Date().toISOString(),
            fallback: true
        };
    }

    /**
     * Get simple hash for backward compatibility
     * (matches old generateBrowserFingerprint behavior)
     */
    async getSimpleHash() {
        const components = [
            navigator.userAgent,
            navigator.language,
            screen.width + 'x' + screen.height,
            screen.colorDepth,
            new Date().getTimezoneOffset(),
            navigator.platform,
            navigator.cookieEnabled
        ];

        let hash = 0;
        const str = components.join('|');
        for (let i = 0; i < str.length; i++) {
            const char = str.charCodeAt(i);
            hash = ((hash << 5) - hash) + char;
            hash = hash & hash;
        }

        return Math.abs(hash).toString(36);
    }
}

// Export singleton instance
export default new AdvancedFingerprint();
